import { TFeature } from "../types/geojson";
import { BASE_IMAGE_WIDTH, SMALL_MARKER_SIZE, SMALL_MARKER_ZOOM, MID_MARKER_ZOOM, MID_MARKER_SIZE, LARGE_MARKER_ZOOM, LARGE_MARKER_SIZE } from "../renderers/base/RenderUtils";
import type { ExpressionSpecification } from "maplibre-gl";

/** Default multiplier stops: 0.62× at zoom 7, 0.85× at zoom 20, 1.15× at zoom 22 */
export const DEFAULT_SIZE_STOPS: [number, number][] = [
	[SMALL_MARKER_ZOOM, SMALL_MARKER_SIZE],
	[MID_MARKER_ZOOM, MID_MARKER_SIZE],
	[LARGE_MARKER_ZOOM, LARGE_MARKER_SIZE],
];

/**
 * Linearly interpolate between keyframe stops at a given zoom.
 * Clamps outside the defined range.
 */
export function interpolateStops(stops: [number, number][], zoom: number): number {
	if (stops.length === 0) return 1;
	if (stops.length === 1) return stops[0][1];
	if (zoom <= stops[0][0]) return stops[0][1];
	if (zoom >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
	for (let i = 0; i < stops.length - 1; i++) {
		const [z0, v0] = stops[i];
		const [z1, v1] = stops[i + 1];
		if (zoom >= z0 && zoom <= z1) {
			const t = (z1 === z0) ? 0 : (zoom - z0) / (z1 - z0);
			return v0 + t * (v1 - v0);
		}
	}
	return stops[stops.length - 1][1];
}

/**
 * Get sorted [zoom, multiplier] entries for a feature.
 * Falls back to the default ramp if no custom iconSizeStops defined.
 */
export function getFeatureStops(feature: TFeature): [number, number][] {
	const custom = feature.properties?.iconSizeStops;
	if (custom && Object.keys(custom).length > 0) {
		return Object.entries(custom)
			.map(([z, v]) => [Number(z), v as number] as [number, number])
			.sort((a, b) => a[0] - b[0]);
	}
	return DEFAULT_SIZE_STOPS;
}

/**
 * Collect the union of all zoom levels used across non-worldSize image shapes.
 * Ensures at least 2 stops exist for a valid interpolation expression.
 */
export function collectUsedZoomStops(features: TFeature[]): number[] {
	const zooms = new Set<number>();
	for (const feature of features) {
		if (feature.properties?.worldSize) continue;
		if (feature.geometry?.type !== "Point" || feature.properties?.shapeType !== "image") continue;
		for (const [z] of getFeatureStops(feature)) zooms.add(z);
	}
	if (zooms.size === 0) {
		zooms.add(SMALL_MARKER_ZOOM);
		zooms.add(LARGE_MARKER_ZOOM);
	}
	if (zooms.size === 1) {
		const z = [...zooms][0];
		zooms.add(z > 1 ? 1 : 22);
	}
	return [...zooms].sort((a, b) => a - b);
}

/**
 * Enrich a single feature with __sz_N properties for every zoom in zoomStops.
 * Non-worldSize image points get per-zoom icon-size values baked in.
 * Other features pass through unchanged.
 */
export function enrichFeatureWithSizeProps(feature: TFeature, zoomStops: number[]): TFeature {
	if (feature.properties?.worldSize) return feature;
	if (feature.geometry?.type !== "Point" || feature.properties?.shapeType !== "image") return feature;

	const featureStops = getFeatureStops(feature);
	const width = feature.properties?.width ?? 5;
	const loadedWidth = feature.properties?.__loadedWidth ?? 800;

	const sizeProps: Record<string, number> = {};
	for (const z of zoomStops) {
		const multiplier = interpolateStops(featureStops, z);
		sizeProps[`__sz_${z}`] = (width * BASE_IMAGE_WIDTH * multiplier) / loadedWidth;
	}

	return {
		...feature,
		properties: { ...feature.properties, ...sizeProps },
	};
}

/**
 * Build an icon-size expression that reads pre-computed __sz_N properties.
 * @param zoomStops - The zoom levels to include in the expression
 * @param prefix - Property prefix ("" for player, "user_" for draw mode)
 */
export function buildIconSizeExpression(zoomStops: number[], prefix = ""): ExpressionSpecification {
	return [
		'interpolate', ['linear'], ['zoom'],
		...zoomStops.flatMap(z => [z, ["number", ["get", `${prefix}__sz_${z}`], 0]])
	] as ExpressionSpecification;
}
